import React, { Children } from 'react';
import { AnimatedSection } from '../components/AnimatedSection';
import { PageBanner } from '../components/PageBanner';
import { PhotoCredit } from '../components/PhotoCredit';
export function HowWeWorkPage() {
  return <div data-id="element-586">
      <PageBanner title="Our Approach" subtitle="Practitioner-led funding, built on 20 years of direct experience" image="https://cdn.magicpatterns.com/uploads/hTKqmVphwySe425vNToFHT/Stairway_Foundation_1.jpg" credit="Photo: Stairway Foundation" data-id="element-587" />

      {/* Intro Section */}
      <section className="py-12 px-4 sm:px-6 lg:px-8" data-id="element-588">
        <div className="max-w-3xl mx-auto" data-id="element-589">
          <AnimatedSection delayClass="delay-200" data-id="element-590">
            <p className="text-lg text-brand-brown leading-relaxed" data-id="element-591">
              Most funders come to this work from the outside. Firetree's team
              members have spent decades founding, running and governing
              nonprofit organisations across Southeast Asia simultaneously
              alongside the funding work.
            </p>
          </AnimatedSection>
          <AnimatedSection delayClass="delay-300" data-id="element-592">
            <p className="text-lg text-brand-brown leading-relaxed mt-6" data-id="element-593">
              This is what we mean by practitioner-led funding. We assess
              organisations not against an arbitrary rubric in isolation, but
              against direct experience of doing the same work ourselves. We
              know what good looks like because we have had to build it, defend
              it, and sometimes rebuild it. Over more than 20 years and $130M
              deployed, that practitioner judgment has shaped every partnership
              we have formed and every decision we have made about where
              resources can do the most good.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Image Stack 2 — Single wide image */}
      <div className="w-full py-4" data-id="element-594">
        <AnimatedSection data-id="element-595">
          <PhotoCredit credit="Photo: Baan Dek Foundation" className="overflow-hidden" data-id="element-596">
            <img src="https://cdn.magicpatterns.com/uploads/nVSNqC29Hg12nC8LoqZ9K5/Baan_Dek_Foundation_2.jpg" alt="Baan Dek Foundation community outreach" className="w-full h-72 md:h-[28rem] object-cover" data-id="element-597" />
          </PhotoCredit>
        </AnimatedSection>
      </div>

      {/* Section: We operate, not just fund */}
      <section className="py-10 px-4 sm:px-6 lg:px-8" data-id="element-598">
        <div className="max-w-3xl mx-auto" data-id="element-599">
          <AnimatedSection data-id="element-600">
            <div className="w-10 h-10 rounded-full bg-brand-card/60 flex items-center justify-center mb-4" data-id="element-601">
              <img src="https://cdn.magicpatterns.com/uploads/abZxQTpLYL87y3EGV2b5y3/Vector-1.svg" alt="" className="w-5 h-5" data-id="element-602" />
            </div>
            <h2 className="text-3xl md:text-4xl font-serif text-brand-charcoal mb-5" data-id="element-603">
              We operate, not just fund
            </h2>
          </AnimatedSection>
          <AnimatedSection delayClass="delay-100" data-id="element-604">
            <p className="text-lg text-brand-brown leading-relaxed" data-id="element-605">
              Tondo Community Initiative in Manila is our child protection
              initiative, serving one of the Philippines' most marginalised
              urban communities. Starfish Education Foundation in Thailand is
              our education innovation initiative working directly with public
              schools, teachers and government agencies. We fully fund and run
              both.
            </p>
          </AnimatedSection>
          <AnimatedSection delayClass="delay-200" data-id="element-606">
            <p className="text-lg text-brand-brown leading-relaxed mt-6" data-id="element-607">
              This matters because it keeps us grounded. We know what rigorous
              child protection looks like from the inside. We know what
              governance under pressure feels like. We know what it costs — in
              time, talent and resources — to deliver quality work at the
              community level. When we assess a partner organisation, we are not
              working from a framework. We are drawing on direct experience of
              doing the same work ourselves.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Section: We've been in the room */}
      <section className="py-10 px-4 sm:px-6 lg:px-8" data-id="element-608">
        <div className="max-w-3xl mx-auto" data-id="element-609">
          <AnimatedSection data-id="element-610">
            <div className="w-10 h-10 rounded-full bg-brand-card/60 flex items-center justify-center mb-4" data-id="element-611">
              <img src="https://cdn.magicpatterns.com/uploads/aKxBeFbaf89724gU4CLWw8/Group_1.svg" alt="" className="w-5 h-5" data-id="element-612" />
            </div>
            <h2 className="text-3xl md:text-4xl font-serif text-brand-charcoal mb-5" data-id="element-613">
              We've been in the room
            </h2>
          </AnimatedSection>
          <AnimatedSection delayClass="delay-100" data-id="element-614">
            <p className="text-lg text-brand-brown leading-relaxed" data-id="element-615">
              Fund are making now — about programme pivots, team management
              under pressure, and moments where the right path forward is
              genuinely unclear. When we sit with a partner, we are not offering
              an outside perspective. We know what it feels like to hold an
              organisation together through difficulty, to balance what a
              community needs against what the resources allow, and to manage
              the gap between what looks good on paper and what is actually
              happening on the ground.
            </p>
          </AnimatedSection>
          <AnimatedSection delayClass="delay-200" data-id="element-616">
            <p className="text-lg text-brand-brown leading-relaxed mt-6" data-id="element-617">
              This is what it means to work as peers. Board experience across
              more than 20 organisations in Southeast Asia deepens that: it
              means we have seen how leadership teams behave when things are
              hard, not just when things are going well. But what positions us
              as peers is more than what we have been and are in those chairs,
              and we bring that into every conversation we have with a partner.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Image Stack 1 — Single wide image */}
      <div className="w-full py-4" data-id="element-618">
        <AnimatedSection data-id="element-619">
          <PhotoCredit credit="Photo: Mith Samlanh" className="overflow-hidden" data-id="element-620">
            <img src="https://i.postimg.cc/02XbnSfs/Mith-Samlanh-1.jpg" alt="Teachers in a training session in the Philippines" className="w-full h-72 md:h-[28rem] object-cover" data-id="element-621" />
          </PhotoCredit>
        </AnimatedSection>
      </div>

      {/* Section: We stay */}
      <section className="py-10 px-4 sm:px-6 lg:px-8" data-id="element-622">
        <div className="max-w-3xl mx-auto" data-id="element-623">
          <AnimatedSection data-id="element-624">
            <div className="w-10 h-10 rounded-full bg-brand-card/60 flex items-center justify-center mb-4" data-id="element-625">
              <img src="https://cdn.magicpatterns.com/uploads/kd2dLXtUb7TN6WLtTMqz8w/Vector-4.svg" alt="" className="w-5 h-5" data-id="element-626" />
            </div>
            <h2 className="text-3xl md:text-4xl font-serif text-brand-charcoal mb-5" data-id="element-627">
              We are in it for the long run
            </h2>
          </AnimatedSection>
          <AnimatedSection delayClass="delay-100" data-id="element-628">
            <p className="text-lg text-brand-brown leading-relaxed" data-id="element-629">
              Before we fund anyone, we spend significant time with the
              leadership, the team, and the communities they serve. We watch how
              decisions get made and how the organisation behaves when things
              are hard. We provide untied grant(s), specifically to create the
              time and space for that vulnerability to develop, on both sides.
              By the time an organisation enters our fund portfolio, we know it
              the way a colleague would.
            </p>
          </AnimatedSection>
          <AnimatedSection delayClass="delay-200" data-id="element-630">
            <p className="text-lg text-brand-brown leading-relaxed mt-6" data-id="element-631">
              Our funding relationships span multiple years. Short funding
              cycles consume the energy that leaders should be spending on their
              work. When an organisation knows its funding relationship is
              stable, it can plan properly, hire the right people, adapt and
              evolve as they see the need to and do all of this in a timely
              manner, without the distraction of worrying about where the next
              resources are coming from. Leaders also trust that a funder will
              not exit the first sign of difficulty, are more likely to share
              the real challenges they face. That openness is what allows us to
              be genuinely useful not just as a source of funding, but as a
              thought partner when situations are complicated.
            </p>
          </AnimatedSection>
          <AnimatedSection delayClass="delay-300" data-id="element-632">
            <p className="text-xl text-brand-charcoal leading-relaxed mt-6 font-semibold font-serif" data-id="element-633">
              We have worked with some of our partners for over 15 years.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Section: We share who we back */}
      <section className="py-10 px-4 sm:px-6 lg:px-8" data-id="element-634">
        <div className="max-w-3xl mx-auto" data-id="element-635">
          <AnimatedSection data-id="element-636">
            <div className="w-10 h-10 rounded-full bg-brand-card/60 flex items-center justify-center mb-4" data-id="element-637">
              <img src="https://cdn.magicpatterns.com/uploads/8gFBbAsy74VHUEiExgZu7g/Vector-3.svg" alt="" className="w-5 h-5" data-id="element-638" />
            </div>
            <h2 className="text-3xl md:text-4xl font-serif text-brand-charcoal mb-5" data-id="element-639">
              We talk about who we fund
            </h2>
          </AnimatedSection>
          <AnimatedSection delayClass="delay-100" data-id="element-640">
            <p className="text-lg text-brand-brown leading-relaxed" data-id="element-641">
              Every organisation in the Firetree Fund portfolio is one we
              currently fund ourselves — and intend to keep funding. These
              aren't recent discoveries. They represent some of the most
              effective and operationally mature organisations in their field,
              and they have been through years of real partnership with us
              before being introduced to fellow funders.
            </p>
          </AnimatedSection>
          <AnimatedSection delayClass="delay-200" data-id="element-642">
            <p className="text-lg text-brand-brown leading-relaxed mt-6" data-id="element-643">
              Organisations with demonstrated impact, strong leadership and
              established accountability mechanisms are considered for inclusion
              in the Firetree Fund portfolio. Before reaching this point, each
              has been through rigorous validation within — assessed for
              financial health, leadership resilience under pressure, learning
              agility and a proven ability to deploy resources effectively.
            </p>
          </AnimatedSection>
          <AnimatedSection delayClass="delay-300" data-id="element-644">
            <p className="text-lg text-brand-brown leading-relaxed mt-6" data-id="element-645">
              We believe successful organisations should not be heavily
              dependent on a single or very few donors. The organisations we
              bring to the Fund maintain diversified funding bases — we would
              fund them more expansively if that principle didn't hold us back.
              They are ready for more, and we know it.
            </p>
          </AnimatedSection>
        </div>
      </section>
    </div>;
}